import { useEffect } from 'react'
import { useStaleBuild } from '../lib/build'
import { Button } from './Button'
import './stale-build.css'
import { APP_NAME } from '@app/shared/brand'

/**
 * What to do when the server has a newer build than this tab.
 *
 * ONE mechanism, TWO policies, and which applies is decided by whether the
 * surface can lose work:
 *
 *  - **A public page holds nothing.** It is read-only, it has the widest
 *    audience, and it is read on a phone in shul — so being out of date is the
 *    only real cost and refreshing has none. It refreshes itself.
 *  - **The back office holds unsaved writing.** The composer is a half-written
 *    message somebody is in the middle of. A reload there is not a refresh, it
 *    is a loss, so it is OFFERED and never imposed.
 *
 * That asymmetry is the whole design; anything else is a preference about
 * notifications.
 *
 * **It is not a quiet notice, and the reason is that a stale tab is not merely
 * out of date.** The API and the bundle ship together, so a field the new
 * server stopped returning is a field the old bundle still reads — and it
 * renders `undefined` into the page. A members list showed every surname as
 * "undefined" exactly that way. What the person is looking at is WRONG, not
 * old, and a notice tucked into a corner is one they will not read before
 * trusting what is on the screen.
 */
export function StaleBuild({ policy }: { policy: 'refresh' | 'offer' }) {
  const stale = useStaleBuild()

  useEffect(() => {
    if (stale && policy === 'refresh') window.location.reload()
  }, [stale, policy])

  if (!stale || policy === 'refresh') return null

  return (
    <div className="stalebuild" role="status">
      <p className="stalebuild__text">
        {APP_NAME} has been updated. This tab is running the old version and may
        show missing or wrong values.
      </p>
      <Button variant="secondary" onClick={() => window.location.reload()}>
        Reload
      </Button>
    </div>
  )
}
